import { cn } from '@vrzl/ui';
import type { HTMLAttributes, ReactNode } from 'react';

import type { OverviewBlockWidth } from '../api/types';
import { GRID_COLUMNS, blockWidthClass, blockWidthDesktopClass } from '../constants';
import { useRowSpan } from '../hooks/useRowSpan';
import { layoutSections } from '../utils/editBlocks';

type ItemProps = Omit<HTMLAttributes<HTMLDivElement>, 'style'> & {
  width: OverviewBlockWidth;
  fixedColumns: boolean;
  children: ReactNode;
};

const widthClass = (width: OverviewBlockWidth, fixedColumns: boolean) =>
  fixedColumns ? blockWidthDesktopClass[width] : blockWidthClass[width];

/** Item in een rij met een kwartblok: houdt de hoogte van zijn kaart (items-start op de rij). */
function RowItem({ width, fixedColumns, className, children, ...props }: ItemProps) {
  return (
    <div
      {...props}
      className={cn(
        'min-w-0 print:break-inside-avoid',
        widthClass(width, fixedColumns),
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Item in een vrij stuk: de hoogte van de kaart wordt gemeten (de binnenste div rekt niet
 * mee) en het item spant daar genoeg rasterrijen voor, zodat het onder een kortere buur
 * omhoog schuift. In print een gewoon raster, omdat gemeten hoogtes op papier niet kloppen.
 */
function FlowItem({ width, fixedColumns, className, children, ...props }: ItemProps) {
  const { ref, span } = useRowSpan<HTMLDivElement>();
  return (
    <div
      {...props}
      className={cn(
        'row-span-(--row-span) min-w-0 print:row-auto print:break-inside-avoid',
        widthClass(width, fixedColumns),
        className,
      )}
      style={{ '--row-span': span ?? 1 } as React.CSSProperties}
    >
      <div ref={ref}>{children}</div>
    </div>
  );
}

type OverviewLayoutProps = {
  /** Breedte per blok, in config-volgorde. */
  widths: readonly OverviewBlockWidth[];
  /** Altijd 4 kolommen (voorbeeld in de editor) i.p.v. 1/2/4 volgens de schermbreedte. */
  fixedColumns?: boolean;
  getKey: (index: number) => string;
  /** Extra props op het rasteritem van een blok (bv. drag-handlers van de editor). */
  itemProps?: (index: number) => HTMLAttributes<HTMLDivElement>;
  /** De kaart van blok `index`. */
  renderItem: (index: number) => ReactNode;
  /** Wordt achter het laatste blok van een rij met een kwartblok gerenderd als die niet vol is. */
  renderRowGap?: (gap: { used: number; empty: number }) => ReactNode;
};

/**
 * Indeling van de Overview, gedeeld met de editor. Blokken staan in config-volgorde van links
 * naar rechts; past een blok niet meer, dan begint een nieuwe rij (geen dense-plaatsing).
 * - Een rij met een kwartblok is een eigen raster: de rij is zo hoog als het hoogste blok, elke
 *   kaart houdt zijn eigen hoogte (items-start, het restant toont de paginaachtergrond) en een
 *   rij die niet vol is blijft zo.
 * - Aaneengesloten rijen zonder kwartblok vormen één meetraster (zie GRID_ROW_PX): blokken
 *   schuiven omhoog onder een kortere buur, dus geen ruimte tussen halve blokken.
 * Kolommen: 1 (mobiel), 2 (md) of 4 (lg); op md is een quarter dus half en een half volledig.
 */
export function OverviewLayout({
  widths,
  fixedColumns = false,
  getKey,
  itemProps,
  renderItem,
  renderRowGap,
}: OverviewLayoutProps) {
  const columns = fixedColumns ? 'grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  const item = (index: number, Item: typeof RowItem) => (
    <Item
      key={getKey(index)}
      width={widths[index]!}
      fixedColumns={fixedColumns}
      {...itemProps?.(index)}
    >
      {renderItem(index)}
    </Item>
  );

  return (
    <div className="flex flex-col gap-4">
      {layoutSections(widths.map((width) => ({ width }))).map((section) => {
        const sectionKey = getKey(section.rows[0]!.indexes[0]!);

        if (section.kind === 'row') {
          const [row] = section.rows;
          const empty = GRID_COLUMNS - row!.used;
          return (
            <div key={sectionKey} className={cn('grid items-start gap-4', columns)}>
              {row!.indexes.map((index) => item(index, RowItem))}
              {empty > 0 && renderRowGap?.({ used: row!.used, empty })}
            </div>
          );
        }

        return (
          // -mb-4: de laatste rijspan bevat 16px gap die er onderaan niet hoort te staan.
          <div
            key={sectionKey}
            className={cn(
              'grid auto-rows-[8px] gap-x-4 -mb-4 print:mb-0 print:auto-rows-auto print:gap-y-4',
              columns,
            )}
          >
            {section.rows.flatMap((row) => row.indexes).map((index) => item(index, FlowItem))}
          </div>
        );
      })}
    </div>
  );
}
